import { useEffect, useState } from 'react';
import { useApp } from '@/lib/core/providers/appProvider';
import { useLocaleStore } from '@/lib/core';
import { useUIStore } from '@/lib/core/stores/useUIStore';
import { useClientTranslation } from '@/lib/core/i18n';
import type {
  EventBookFlowConfirmationPropertiesModel,
  EventRegistrationDto,
  ImageWithCrops,
  ReservationDetailDto,
} from '@/services/platform/api/model';
import {
  formatDate,
  formatTime,
  formatTimeFromDate,
  getFirstName,
  ImageMap,
  ImagesToImageMap,
  timePartIconFromDate,
  timePartIconFromTime,
} from '@/lib/core/utils';
import { ConfirmationBanner } from '@/components/shared/booking/common/confirmation/banner';
import { useExploreVenueDetail } from '@/services/platform/data/explore';
import { getReservation } from '@/services/platform/data/booking';
import { useBookingShare } from '@/lib/core/utils/share';
import { Icon } from '@/components/ui/icon';
import { resolveOccurrenceTime } from './derive';
import type { BookingState } from './types';

/**
 * Registration states that still need the concierge to close the loop (payment, capacity).
 * `PendingSync` is the local fallback persisted while MuleSoft was unavailable — the registration
 * does not exist in Salesforce yet, so it belongs here rather than reading as confirmed.
 */
const PENDING_STATUSES = new Set(['PendingPayment', 'Waitlisted', 'PendingSync']);
/**
 * A registration that did not go through — the payment succeeded, the booking did not. Without
 * this, both statuses fell through to the default branch and rendered as a full confirmation.
 */
const FAILED_STATUSES = new Set(['Cancelled', 'CancellationRequested']);

export interface EventRegistrationConfirmationProps {
  booking: BookingState;
  registration: EventRegistrationDto;
  cmsProps?: EventBookFlowConfirmationPropertiesModel;
  onGoHome: () => void;
  onSeeBookingDetails: () => void;
}

/**
 * Confirmation for `bookingMode === 'Salesforce'` registrations. Not a drop-in reuse of
 * `ConfirmationBook`/`ConfirmationRequest` (SevenRooms) — those key off `reservation.publicGuid`
 * (share, cancel), which `EventRegistrationDto` doesn't have. Branches on the returned `status`:
 * `Confirmed` reads like a normal booking confirmation; `PendingPayment`/`Waitlisted`/`PendingSync`
 * reuse the same "waiting on the concierge" copy the SevenRooms Pending path already shows via
 * `ConfirmationRequest`; `Cancelled`/`CancellationRequested` show a distinct failure state.
 *
 * For a paid event, `registration` is not necessarily the server's own record — see the preselect
 * effect in `flows/event/index.tsx`, which prefers the server-verified `EventDetailDto.myRegistration`
 * when it is available for the occurrence just paid for, and only falls back to a client-fabricated
 * `Confirmed` while that write is still catching up with the payment.
 */
export function EventRegistrationConfirmation({
  booking,
  registration,
  cmsProps,
  onGoHome,
  onSeeBookingDetails,
}: EventRegistrationConfirmationProps) {
  const t = useClientTranslation();
  const locale = useLocaleStore(state => state.locale);
  const { user, appSettings } = useApp();
  const setMainClassName = useUIStore(state => state.setMainClassName);

  useEffect(() => {
    setMainClassName('pb-0!');
    return () => setMainClassName('');
  }, [setMainClassName]);

  const isPending = PENDING_STATUSES.has(registration.status ?? '');
  const isFailed = FAILED_STATUSES.has(registration.status ?? '');
  const firstName = getFirstName(user?.name) ?? null;
  const canSeeBookingDetails = !isPending && !isFailed && !!registration.id;

  // `booking` (the flow's own locally-assembled state) is usually right, but it is a second,
  // independent path to the same facts the server already holds against `registration.id` — the
  // same endpoint the "See booking details" page uses. Once confirmed, prefer that authoritative
  // response for the displayed rows/logo/image; `booking` remains the fallback while it's loading
  // (or for `PendingSync`, which has no id to fetch with yet).
  const [reservationDetail, setReservationDetail] = useState<ReservationDetailDto>();
  useEffect(() => {
    if (!canSeeBookingDetails || !registration.id) return;
    let isStale = false;
    getReservation(registration.id, locale).then(data => {
      if (!isStale && data) setReservationDetail(data);
    });
    return () => {
      isStale = true;
    };
  }, [canSeeBookingDetails, registration.id, locale]);

  const eventImage = reservationDetail?.event?.imageUrl
    ? { src: reservationDetail.event.imageUrl, alt: reservationDetail.event.title ?? '' }
    : booking.event?.imageUrl
      ? { src: booking.event.imageUrl, alt: booking.event.title ?? '' }
      : ImagesToImageMap(appSettings?.properties?.eventNoPicImage, 'bookingDetailHero');
  // The occurrence's venue is authoritative (contract §3.1.1) — an event can run one date at
  // one venue and another date at a different one.
  const venueDisplay =
    reservationDetail?.venue?.name ?? booking.occurrence?.venueName ?? booking.venue?.name ?? '';
  const guestCount = reservationDetail?.guestCount ?? booking.guestCount;
  const guestDisplay =
    guestCount !== null && guestCount !== undefined ? `${guestCount} ${t('App.Labels.Guests')}` : '';
  const dateDisplay = reservationDetail?.startDate
    ? formatDate(reservationDetail.startDate, locale)
    : booking.date
      ? formatDate(booking.date.toDateString(), locale)
      : '';
  // No SevenRooms room/time picker for this (Salesforce) path — the occurrence's own time,
  // never a user choice, is the fallback whenever `booking.time` wasn't otherwise pre-filled.
  const resolvedTime = booking.time ?? resolveOccurrenceTime(booking.occurrence);
  const timeDisplay = reservationDetail?.startDate
    ? formatTimeFromDate(reservationDetail.startDate)
    : resolvedTime
      ? formatTime(resolvedTime)
      : '';
  const timeIcon = reservationDetail?.startDate
    ? timePartIconFromDate(reservationDetail.startDate)
    : timePartIconFromTime(resolvedTime);

  // `booking.venue` (see `derive.ts`) is built ad hoc from event data and carries no logo —
  // unlike sunbed/stay's `VenueDto`, which comes from a full venues API response. `reservationDetail.venue`
  // (above) already carries one once confirmed; this covers `PendingSync`, which has no id to
  // fetch a reservation with yet, by fetching the venue directly instead.
  const venueNodeKey = booking.occurrence?.venueNodeKey ?? booking.venue?.venueNodeKey ?? '';
  const { venue: venueDetail } = useExploreVenueDetail(venueNodeKey);
  const venueLogo = ImageMap((reservationDetail?.venue?.logo ?? venueDetail?.logo) as ImageWithCrops);

  const { handleShare } = useBookingShare({
    publicGuid: registration.id,
    name: booking.event?.title,
    venue: { name: venueDisplay },
  });

  return (
    <ConfirmationBanner
      image={eventImage}
      logo={venueLogo}
      firstName={firstName}
      title={
        isFailed
          ? t('App.Labels.BookingFailedText')
          : isPending
            ? t('App.Labels.BookingRequestConfirmationText')
            : (cmsProps?.title ?? t('App.Labels.BookingConfirmedText'))
      }
      subtitle={
        isFailed
          ? t('App.Labels.BookingFailedSubtitle')
          : isPending
            ? t('App.Labels.BookingRequestConfirmationWaiting')
            : undefined
      }
      rows={[
        [
          ...(dateDisplay ? [{ text: dateDisplay }] : []),
          ...(timeDisplay ? [{ icon: timeIcon, text: timeDisplay }] : []),
        ],
        [
          ...(venueDisplay ? [{ icon: 'building', text: venueDisplay }] : []),
          ...(guestDisplay ? [{ icon: 'group', text: guestDisplay }] : []),
        ],
      ]}
      actions={[
        {
          label: cmsProps?.goBackLabel ?? t('App.Labels.GoBackHome'),
          onClick: onGoHome,
          variant: isPending || isFailed || canSeeBookingDetails ? 'secondary-outline' : 'primary-solid',
        },
        ...(canSeeBookingDetails
          ? [
              {
                label: cmsProps?.seeBookingDetailsLabel ?? t('App.Labels.SeeBookingDetails'),
                onClick: onSeeBookingDetails,
                variant: 'primary-solid' as const,
              },
            ]
          : []),
      ]}
      topPanel={
        canSeeBookingDetails ? (
          <div className="pt-safe-area flex items-center justify-end px-4">
            <button
              type="button"
              className="flex size-10 items-center justify-center"
              aria-label={t('App.Labels.ShareGuests')}
              onClick={handleShare}
            >
              <Icon name="ShareIos" width={24} height={24} strokeWidth={1.5} className="text-white" />
            </button>
          </div>
        ) : undefined
      }
      className="-mt-[max(1rem,env(safe-area-inset-top))]!"
    />
  );
}
